import { createSlice, PayloadAction } from '@reduxjs/toolkit'

export interface RealtimeState {
  isConnected: boolean
  connectionStatus: 'connecting' | 'connected' | 'disconnected' | 'error'
  lastUpdate: string | null
  dashboardMetrics: {
    totalOrders: number
    activeOrders: number
    completedOrders: number
    totalEmployees: number
    activeEmployees: number
    totalRevenue: number
    monthlyRevenue: number
    productionEfficiency: number
    orderCompletionRate: number
    employeeUtilization: number
    lastUpdated: string | null
  }
  recentActivities: Array<{
    id: string
    type: 'order_created' | 'order_updated' | 'employee_created' | 'employee_updated' | 'assignment_created'
    title: string
    description: string
    timestamp: string
    userId?: number
    entityId?: number
  }>
  systemAlerts: Array<{
    id: string
    type: 'info' | 'warning' | 'error' | 'success'
    title: string
    message: string
    timestamp: string
    acknowledged: boolean
  }>
}

const initialState: RealtimeState = {
  isConnected: false,
  connectionStatus: 'disconnected',
  lastUpdate: null,
  dashboardMetrics: {
    totalOrders: 0,
    activeOrders: 0,
    completedOrders: 0,
    totalEmployees: 0,
    activeEmployees: 0,
    totalRevenue: 0,
    monthlyRevenue: 0,
    productionEfficiency: 0,
    orderCompletionRate: 0,
    employeeUtilization: 0,
    lastUpdated: null,
  },
  recentActivities: [],
  systemAlerts: [],
}

const realtimeSlice = createSlice({
  name: 'realtime',
  initialState,
  reducers: {
    setConnectionStatus: (state, action: PayloadAction<RealtimeState['connectionStatus']>) => {
      state.connectionStatus = action.payload
      state.isConnected = action.payload === 'connected'
    },
    updateDashboardMetrics: (state, action: PayloadAction<Partial<RealtimeState['dashboardMetrics']>>) => {
      state.dashboardMetrics = { ...state.dashboardMetrics, ...action.payload }
      state.lastUpdate = new Date().toISOString()
    },
    addRecentActivity: (state, action: PayloadAction<Omit<RealtimeState['recentActivities'][0], 'id'>>) => {
      const activity = {
        id: Date.now().toString(),
        ...action.payload,
      }
      state.recentActivities.unshift(activity)
      // Keep only last 50 activities
      if (state.recentActivities.length > 50) {
        state.recentActivities = state.recentActivities.slice(0, 50)
      }
      state.lastUpdate = new Date().toISOString()
    },
    addSystemAlert: (state, action: PayloadAction<Omit<RealtimeState['systemAlerts'][0], 'id' | 'acknowledged'>>) => {
      const alert = {
        id: Date.now().toString(),
        ...action.payload,
        acknowledged: false,
      }
      state.systemAlerts.unshift(alert)
      // Keep only last 20 alerts
      if (state.systemAlerts.length > 20) {
        state.systemAlerts = state.systemAlerts.slice(0, 20)
      }
      state.lastUpdate = new Date().toISOString()
    },
    acknowledgeAlert: (state, action: PayloadAction<string>) => {
      const alert = state.systemAlerts.find(a => a.id === action.payload)
      if (alert) {
        alert.acknowledged = true
      }
    },
    acknowledgeAllAlerts: (state) => {
      state.systemAlerts.forEach(alert => {
        alert.acknowledged = true
      })
    },
    removeAlert: (state, action: PayloadAction<string>) => {
      state.systemAlerts = state.systemAlerts.filter(a => a.id !== action.payload)
    },
    clearAlerts: (state) => {
      state.systemAlerts = []
    },
    clearActivities: (state) => {
      state.recentActivities = []
    },
    // WebSocket message handlers
    handleOrderCreated: (state, action: PayloadAction<any>) => {
      state.dashboardMetrics.totalOrders += 1
      state.dashboardMetrics.activeOrders += 1
      state.dashboardMetrics.lastUpdated = new Date().toISOString()
      
      const activity = {
        id: Date.now().toString(),
        type: 'order_created' as const,
        title: 'New Order Created',
        description: `Order #${action.payload.order_number} has been created`,
        timestamp: new Date().toISOString(),
        entityId: action.payload.order_id,
      }
      state.recentActivities.unshift(activity)
      state.lastUpdate = new Date().toISOString()
    },
    handleOrderUpdated: (state, action: PayloadAction<any>) => {
      const activity = {
        id: Date.now().toString(),
        type: 'order_updated' as const,
        title: 'Order Updated',
        description: `Order #${action.payload.order_number} has been updated`,
        timestamp: new Date().toISOString(),
        entityId: action.payload.order_id,
      }
      state.recentActivities.unshift(activity)
      state.lastUpdate = new Date().toISOString()
    },
    handleOrderStatusChanged: (state, action: PayloadAction<any>) => {
      if (action.payload.new_status === 'completed') {
        state.dashboardMetrics.completedOrders += 1
        state.dashboardMetrics.activeOrders = Math.max(0, state.dashboardMetrics.activeOrders - 1)
      }
      state.dashboardMetrics.lastUpdated = new Date().toISOString()
      
      const activity = {
        id: Date.now().toString(),
        type: 'order_updated' as const,
        title: 'Order Status Changed',
        description: `Order status changed to ${action.payload.new_status}`,
        timestamp: new Date().toISOString(),
        entityId: action.payload.order_id,
      }
      state.recentActivities.unshift(activity)
      state.lastUpdate = new Date().toISOString()
    },
    handleEmployeeCreated: (state, action: PayloadAction<any>) => {
      state.dashboardMetrics.totalEmployees += 1
      if (action.payload.status === 'active') {
        state.dashboardMetrics.activeEmployees += 1
      }
      state.dashboardMetrics.lastUpdated = new Date().toISOString()
      
      const activity = {
        id: Date.now().toString(),
        type: 'employee_created' as const,
        title: 'New Employee Added',
        description: `${action.payload.full_name} has been added to the system`,
        timestamp: new Date().toISOString(),
        entityId: action.payload.employee_id,
      }
      state.recentActivities.unshift(activity)
      state.lastUpdate = new Date().toISOString()
    },
    handleEmployeeUpdated: (state, action: PayloadAction<any>) => {
      const activity = {
        id: Date.now().toString(),
        type: 'employee_updated' as const,
        title: 'Employee Updated',
        description: `${action.payload.full_name} information has been updated`,
        timestamp: new Date().toISOString(),
        entityId: action.payload.employee_id,
      }
      state.recentActivities.unshift(activity)
      state.lastUpdate = new Date().toISOString()
    },
  },
})

export const {
  setConnectionStatus,
  updateDashboardMetrics,
  addRecentActivity,
  addSystemAlert,
  acknowledgeAlert,
  acknowledgeAllAlerts,
  removeAlert,
  clearAlerts,
  clearActivities,
  handleOrderCreated,
  handleOrderUpdated,
  handleOrderStatusChanged,
  handleEmployeeCreated,
  handleEmployeeUpdated,
} = realtimeSlice.actions

export default realtimeSlice.reducer
